<template>
  <div class="kv-ve-image" :class="{ 'is-selected': selected }">
    <div
      v-if="block.settings.src"
      class="kv-ve-image__img-wrap"
      :style="{ textAlign: alignCss }"
    >
      <div class="kv-ve-image__inner" :style="outerWidthStyle">
        <div v-if="aspectRatio !== 'auto'" class="kv-ve-image__ratio" :style="ratioStyle">
          <img
            :src="block.settings.src"
            :alt="block.settings.alt || ''"
            class="kv-ve-image__img kv-ve-image__img--ratio"
            :style="imgStyle"
          />
        </div>
        <img
          v-else
          :src="block.settings.src"
          :alt="block.settings.alt || ''"
          class="kv-ve-image__img"
          :style="imgStyle"
        />
      </div>
    </div>
    <div
      v-else
      class="kv-ve-image__placeholder"
      :style="{
        fontFamily: 'var(--kv-ve-font-family, Helvetica, Arial, sans-serif)',
        borderRadius: borderRadiusPx,
      }"
    >
      <div class="kv-ve-image__inner" :style="outerWidthStyle">
        <div class="kv-ve-image__ratio kv-ve-image__ratio--placeholder" :style="placeholderRatioStyle">
          <div class="kv-ve-image__placeholder-inner">{{ t('double_click_to_add_image') }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VeImageBlock",
  props: {
    block: { type: Object, required: true },
    selected: { type: Boolean, default: false },
  },
  computed: {
    widthMode() {
      const v = String(this.block?.settings?.widthMode || "").toLowerCase();
      if (v === "px" || v === "pct") return v;
      // Default: % for block, px for inline-block
      return this.displayMode === "inline-block" ? "px" : "pct";
    },
    displayMode() {
      const v = String(this.block?.settings?.display || "").toLowerCase();
      // Back-compat: "inline" means "inline-block"
      return v === "inline-block" || v === "inline" ? "inline-block" : "block";
    },
    alignCss() {
      const v = String(this.block?.settings?.align || "").toLowerCase();
      return v === "left" || v === "center" || v === "right" ? v : null;
    },
    align() {
      const v = String(this.block?.settings?.align || "").toLowerCase();
      return v === "left" || v === "center" || v === "right" ? v : null;
    },
    widthPx() {
      const n = Number(this.block?.settings?.widthPx);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    widthPct() {
      const n = Number(this.block?.settings?.widthPct);
      if (!Number.isFinite(n)) return 100;
      return Math.min(100, Math.max(10, Math.round(n)));
    },
    borderWidthPx() {
      const n = Number(this.block?.settings?.borderWidth ?? 0);
      return Number.isFinite(n) && n > 0 ? `${Math.round(n)}px` : "0px";
    },
    borderColor() {
      return this.block?.settings?.borderColor || "#e5e7eb";
    },
    borderStyle() {
      const w = Number(this.block?.settings?.borderWidth ?? 0);
      if (!Number.isFinite(w) || w <= 0) return "0";
      return `${Math.round(w)}px solid ${this.borderColor}`;
    },
    borderRadiusPx() {
      const br = this.block?.settings?.borderRadius ?? 0;
      // Support both number and object (custom corners)
      if (typeof br === "object" && !Array.isArray(br)) {
        const topLeft = Number(br.topLeft) || 0;
        const topRight = Number(br.topRight) || 0;
        const bottomLeft = Number(br.bottomLeft) || 0;
        const bottomRight = Number(br.bottomRight) || 0;
        return `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
      }
      const n = Number(br);
      return Number.isFinite(n) && n >= 0 ? `${n}px` : "0px";
    },
    objectFit() {
      return "cover";
    },
    aspectRatio() {
      return this.block?.settings?.aspectRatio || "auto";
    },
    effectiveAspectRatio() {
      // Empty state: default to widescreen unless user picked a ratio explicitly.
      const hasSrc = Boolean(this.block?.settings?.src);
      const ar = this.aspectRatio;
      if (!hasSrc && ar === "auto") return "16 / 9";
      return ar;
    },
    ratioStyle() {
      return {
        width: "100%",
        maxWidth: null,
        overflow: "hidden",
        borderRadius: this.borderRadiusPx,
        border: this.borderStyle,
        boxSizing: "border-box",
        aspectRatio: this.effectiveAspectRatio,
      };
    },
    placeholderRatioStyle() {
      // Empty state: show a dashed border around the *actual image area* (respects widthPct/widthPx + aspect ratio)
      return {
        ...this.ratioStyle,
        border: "1px dashed #d0d5dd",
        background: "#f9fafb",
      };
    },
    outerWidthStyle() {
      // Width constraint (widthPct/widthPx) applied to the rendered image box.
      const w = this.widthPx;
      const pct = this.widthPct;
      const width = this.widthMode === "px" ? (w ? `${w}px` : null) : (pct < 100 ? `${pct}%` : null);
      if (!width) return {};
      const wantsInlineBlockForInherit = this.displayMode === "block" && !this.align;
      const marginLeft =
        this.displayMode === "block" && this.align
          ? (this.align === "right" ? "auto" : (this.align === "center" ? "auto" : "0"))
          : null;
      const marginRight =
        this.displayMode === "block" && this.align
          ? (this.align === "left" ? "auto" : (this.align === "center" ? "auto" : "0"))
          : null;
      return {
        width,
        maxWidth: "100%",
        display: wantsInlineBlockForInherit ? "inline-block" : (this.displayMode === "block" ? "block" : "inline-block"),
        marginLeft,
        marginRight,
      };
    },
    imgStyle() {
      const w = this.widthPx;
      const isInline = this.displayMode !== "block";
      return {
        width: isInline ? "100%" : "100%",
        maxWidth: "100%",
        display: "block",
        border: this.aspectRatio === "auto" ? this.borderStyle : "0",
        boxSizing: "border-box",
        borderRadius: this.aspectRatio === "auto" ? this.borderRadiusPx : "0px",
        objectFit: this.objectFit,
      };
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    onDoubleClick() {
      this.$emit("double-click", this.block);
    },
  },
};
</script>

<style scoped lang="less">
.kv-ve-image__placeholder {
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667085;
  font-size: 13px;
  padding: 0;
  border-radius: inherit;
}
.kv-ve-image__placeholder-inner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}
.kv-ve-image__img {
  width: 100%;
  height: auto;
  display: inline-block;
  border-radius: 0;
  object-fit: cover;
}
.kv-ve-image__img--ratio {
  height: 100%;
  display: block;
}
.kv-ve-image__inner {
  width: 100%;
}
</style>


